import React, { useEffect, useState, useCallback } from 'react';
import { Star, Check, X, Trash2, Loader2, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const StarRow = ({ value = 0, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        className={n <= value ? 'fill-primary stroke-primary' : 'stroke-muted-foreground/40'}
      />
    ))}
  </span>
);

const STATUS_LABEL = {
  pending: { label: 'En attente', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Publié', className: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800' },
};

const AdminReviewsPanel = () => {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null); // id currently being moderated

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API_BASE}/admin/reviews${qs}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Chargement impossible');
      const data = await res.json();
      setReviews(data.reviews || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (id, status) => {
    setActioning(id);
    try {
      const res = await fetch(`${API_BASE}/admin/reviews/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Modération échouée');
      toast.success(status === 'approved' ? 'Avis publié' : 'Avis rejeté');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActioning(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    setActioning(id);
    try {
      const res = await fetch(`${API_BASE}/admin/reviews/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Suppression échouée');
      toast.success('Avis supprimé');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-reviews-panel">
      {/* Filter bar with counts */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {['pending', 'approved', 'rejected'].map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            className="rounded-none"
            onClick={() => setStatusFilter(s)}
            data-testid={`filter-reviews-${s}`}
          >
            {STATUS_LABEL[s].label} ({counts[s] ?? 0})
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun avis dans cette catégorie.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => {
            const badge = STATUS_LABEL[r.status] || STATUS_LABEL.pending;
            const date = r.created_at ? new Date(r.created_at).toLocaleString('fr-FR') : '';
            return (
              <Card key={r.id} className="rounded-none" data-testid={`review-row-${r.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center flex-wrap gap-3">
                    <StarRow value={r.rating} />
                    <span className="text-sm font-medium">{r.user_name}</span>
                    <span className="text-xs text-muted-foreground">Produit #{r.product_id}</span>
                    <span className="text-xs text-muted-foreground">· {date}</span>
                    <span className={`text-xs px-2 py-0.5 ${badge.className}`}>{badge.label}</span>
                  </div>
                  <h4 className="font-semibold text-sm">{r.title}</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{r.comment}</p>
                  <div className="flex gap-2 pt-2">
                    {r.status !== 'approved' && (
                      <Button
                        size="sm"
                        onClick={() => moderate(r.id, 'approved')}
                        disabled={actioning === r.id}
                        className="rounded-none"
                        data-testid={`approve-review-${r.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Publier
                      </Button>
                    )}
                    {r.status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moderate(r.id, 'rejected')}
                        disabled={actioning === r.id}
                        className="rounded-none"
                        data-testid={`reject-review-${r.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Rejeter
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(r.id)}
                      disabled={actioning === r.id}
                      className="rounded-none text-red-600 hover:text-red-700"
                      data-testid={`delete-review-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviewsPanel;
